/**
 * `WeakIntern` — the intern table behind `SheetId.of` and `RowId.of`:
 * one instance per canonical spelling for as long as anything holds it, and
 * nothing held on the table's account.
 *
 * An entry is a `WeakRef`, so the table never keeps an id alive: once no
 * caller holds an id, the id is collected and a `FinalizationRegistry`
 * callback drops its spellings. Identity (`===`, `Map` keys, `equals`) is
 * what interning is for, and it holds wherever it can be observed — two ids
 * can only be compared while both are held, and a held id is never
 * collected, so every parse of its spelling answers it. An id parsed again
 * after it was collected is a new instance that nothing can tell apart from
 * the old one. The table's size is therefore bounded by the ids in use, not
 * by every id the process ever parsed.
 */

import type { Option } from "../core/option.ts";

export class WeakIntern<V extends object> {
  /** Every spelling seen, to the instance it interned to. */
  readonly #bySpelling = new Map<string, WeakRef<V>>();
  /** Drops a collected instance's spellings (unless a spelling was re-interned since). */
  readonly #cleanup = new FinalizationRegistry<string>((spelling) => {
    if (this.#bySpelling.get(spelling)?.deref() === undefined) this.#bySpelling.delete(spelling);
  });

  /** The live instance interned under `spelling`, or `null`. */
  get(spelling: string): Option<V> {
    return this.#bySpelling.get(spelling)?.deref() ?? null;
  }

  /** Intern `value` under `spelling` (one call per spelling it answers to). */
  set(spelling: string, value: V): void {
    this.#bySpelling.set(spelling, new WeakRef(value));
    this.#cleanup.register(value, spelling);
  }

  /** How many spellings the table holds (collected ones leave once their cleanup runs). */
  get size(): number {
    return this.#bySpelling.size;
  }
}
